import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Icon } from '~/components/atomic';
import { useCopyCharacterMutation } from '~/features/chatRoom';
import { useModal } from '~/hooks';
import { ModalParams } from '~/types';
import { useUserStore } from '~/store';

interface RightMenuProps {
  setShowBlockCharacter: (state: boolean) => void;
}

export default function RightMenu({ setShowBlockCharacter }: RightMenuProps) {
  const { id: character_id } = useParams();
  const email = useUserStore((state) => state.email);
  const { openModal } = useModal();

  const [showSubMenu, setShowSubMenu] = useState(false);
  const submenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!submenuRef.current) return;
      if (!submenuRef.current.contains(e.target as Node)) {
        setShowSubMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setShowSubMenu(false);
    };
  }, []);

  const { mutate, isPending } = useCopyCharacterMutation();

  const modalData: ModalParams = {
    title: 'Saved',
    content:
      'You can find it under My avatar in the generate tab. persona are excluded.',
    type: 'single',
    buttonName: 'Okay',
  };

  const handleCopyCharacter = () => {
    if (!character_id) return;
    mutate(
      { character_id },
      {
        onSuccess: ({ status }) => {
          openModal(modalData);
        },
      },
    );
  };

  return (
    <ButtonBox ref={submenuRef}>
      <Button onClick={() => setShowSubMenu(!showSubMenu)}>
        <Icon color='content_light_1' size={28} iconName='MoreHorizontal' />
      </Button>
      {showSubMenu && (
        <>
          <Container>
            <Wrapper>
              <SubMenu onClick={handleCopyCharacter}>
                <span>Copy avatar</span>
              </SubMenu>
              <SubMenu onClick={() => setShowBlockCharacter(true)}>
                <span>Block / Report character</span>
              </SubMenu>
            </Wrapper>
          </Container>
        </>
      )}
    </ButtonBox>
  );
}

export const ButtonBox = styled.div`
  position: relative;
  width: 44px;
`;

export const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
`;

export const Container = styled.div`
  position: absolute;
  z-index: 5;
  top: 52px;
  right: 15px;
`;

export const Wrapper = styled.ul`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    min-width: 240px;
    max-width: calc(100% - 30px);
    padding: 4px;
    overflow: hidden;
    border: 1px solid ${theme.stroke.stroke_1};
    border-radius: 8px;
    background: ${theme.backgroundColor.bg_dark_2};
  `}
`;

export const SubMenu = styled.li`
  ${({ theme }) => css`
    display: flex;
    position: relative;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 44px;
    padding: 0 8px;
    transition: background 0.1s ease-in-out;
    background: transparent;
    cursor: pointer;
    gap: 8px;

    &:hover,
    &:active {
      background: ${theme.backgroundColor.bg_dark_3};
    }

    span {
      color: ${theme.content.content_light_1};
      ${theme.fontStyle.text.body16_medium};
    }

    &.red {
      span {
        color: ${theme.themeColor.negative};
      }
    }
  `}
`;
